import {MutableRefObject, useContext, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {Box, Button} from '@mui/material';
import {useTranslation} from 'react-i18next';
import HistoryIcon from '@mui/icons-material/History';
import {useAuth} from '../../contexts/auth/context';
import {SnackBarContext} from '../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../contexts/snack-bar/types';
import {RelationshipInfo} from '../../backend/interfaces/relationship-info';
import {UsersRepository} from '../../repositories/users';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {translations} from '../../i18n/translations';
import {usePaginatedTable} from '../../hooks/paginated-table';
import {PaginatedTable} from '../paginated-table/paginated-table';
import EmailIcon from '@mui/icons-material/Email';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import GenericHeader from '../generic-header/generic-header';
import './senior-caregivers-listing.scss';
import {SeniorCaregiversListingModal} from './senior-caregivers-listing-modal';
import {CaregiverMode, ScreenPaths} from '../../enums';
import {UserType} from '../../backend/enums/user-type';
import {RelationshipDirection} from '../../backend/enums/relationship-direction';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  RelationshipType = 'relationship-type',
  Remove = 'remove',
}

type SeniorCaregiversListingProps = {
  seniorId: string;
  seniorName: string;
  invitesHistoryUrl: string;
  inviteCaregiverUrl: string;
  caregiverRowBaseLinkUrl?: string;
  caregiverMode: CaregiverMode;
  entityId?: string;
  url: string;
};

/**
 * Listing of all the caregivers of a senior. On this screen, it's possible to remove
 * relationships and navigate to the invites history and send invite screens.
 *
 * This form was created taking into account that it will exist under the senior tabs.
 * Later on, if it's necessary, we can extend this component to be applied to more contexts.
 */
export const SeniorCaregiversListing = ({
  seniorId,
  seniorName,
  invitesHistoryUrl,
  inviteCaregiverUrl,
  caregiverRowBaseLinkUrl,
  caregiverMode,
  entityId,
  url,
}: SeniorCaregiversListingProps) => {
  const [removeDialogOpened, setRemoveDialogOpened] = useState<boolean>(false);
  const [submittingRemove, setSubmittingRemove] = useState<boolean>(false);
  const relationshipToRemoveRef: MutableRefObject<{id: string; name: string} | undefined> =
    useRef();

  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const {state: authState, removeRelationship, hasWritePermission} = useAuth();
  const canWrite = hasWritePermission(RolePermissionType.Seniors_PersonalData_Caregivers);
  const {t} = useTranslation();
  let history = useHistory();

  // Fetch caregivers
  const handleGetCaregivers = async (limit: number, skip: number) => {
    // Load caregivers
    const result: {totalCount: number; data: Array<RelationshipInfo>} =
      await UsersRepository.instance.getUserRelationships(seniorId, limit, skip, {
        direction: RelationshipDirection.CaregiverToSenior,
      });

    // Process data
    const processedData: PaginatedTableData<RelationshipInfo> = result.data
      // Analyze data
      .filter((obj) => {
        // Only show caregivers
        return obj.withUser.type === UserType.Caregiver;
      })
      // Transform data
      .map((obj) => {
        // Return row values
        return {
          id: obj.id || '',
          item: obj,
          avatarUrl: obj.withUser.picture,
          columns: [
            {
              columnId: ColumnIdentifier.Name,
              value: obj.withUser.name || '',
            },
            {
              columnId: ColumnIdentifier.RelationshipType,
              value: obj.type ? t(translations.relationship_types[obj.type]) : '',
            },
            {
              columnId: ColumnIdentifier.Remove,
            },
          ],
          toLink: caregiverRowBaseLinkUrl
            ? caregiverRowBaseLinkUrl.charAt(caregiverRowBaseLinkUrl.length - 1) === '/'
              ? `${caregiverRowBaseLinkUrl}${obj.withUser.id}`
              : `${caregiverRowBaseLinkUrl}/${obj.withUser.id}`
            : undefined,
        };
      });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {data, isLoading, error, pagesCount, currentPage, handlePageChange, handleApplyFilters} =
    usePaginatedTable<RelationshipInfo>({
      fetchData: handleGetCaregivers,
    });

  const handleAskRemoveRelationship = (relationshipId: string) => {
    // Get the caregiver name
    const caregiverName: string | undefined = data
      .find((obj) => obj.id === relationshipId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set relationship for later
    relationshipToRemoveRef.current = {
      id: relationshipId,
      name: caregiverName || '',
    };

    // Open modal
    setRemoveDialogOpened(true);
  };

  const handleRemoveRelationship = async () => {
    setSubmittingRemove(true);
    try {
      // Make the request to the backend
      await UsersRepository.instance.deleteUserRelationship(
        relationshipToRemoveRef.current?.id || ''
      );

      // Refresh page
      if (data.length - 1 === 0 && currentPage > 1) {
        // Refresh to the previous page because the current page will be empty
        handleApplyFilters(currentPage - 1);
      } else {
        // Refresh the same page because we still have data on it
        handleApplyFilters();
      }

      // What if the current user just removed their relationship with the senior?
      // We need to update the current user auth session if it's the case
      if (
        relationshipToRemoveRef.current &&
        relationshipToRemoveRef.current.id &&
        authState.session?.relationships &&
        Array.isArray(authState.session.relationships) &&
        authState.session.relationships.find(
          (obj) => obj.id === (relationshipToRemoveRef.current as any).id
        )
      ) {
        // Remove relationship in context
        removeRelationship(relationshipToRemoveRef.current.id);
      }

      console.debug(`🟢 Relationship removed.`, {
        relationshipId: relationshipToRemoveRef.current?.id,
        seniorId: seniorId,
        seniorName: seniorName,
        caregiverName: relationshipToRemoveRef.current?.name || '',
      });
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.senior_caregivers_listing.remove_relationship_error_message),
      });
      setSubmittingRemove(false);
      setRemoveDialogOpened(false);
      relationshipToRemoveRef.current = undefined;
    } catch (error) {
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.senior_caregivers_listing.remove_relationship_error_message),
      });

      console.debug(`🔴 Could not remove the relationship.`, {
        relationshipId: relationshipToRemoveRef.current?.id,
        seniorId: seniorId,
        seniorName: seniorName,
        caregiverName: relationshipToRemoveRef.current?.name || '',
        error: error,
      });

      setSubmittingRemove(false);
      setRemoveDialogOpened(false);
      relationshipToRemoveRef.current = undefined;
    }
  };

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      {caregiverMode === CaregiverMode.Professional && entityId && (
        <GenericHeader
          titleCrumbs={[
            {
              label: t(translations.screen_paths.professional_caregiver_seniors),
              link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}`,
            },
            {
              label: seniorName || '',
              link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${seniorId}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Profile}`,
            },
            {
              label: t(translations.screen_paths.senior_personal_data),
              link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${seniorId}${ScreenPaths.Senior_PersonalData}`,
            },
            {
              label: t(translations.screen_paths.senior_caregivers),
              link: url,
            },
          ]}
          isLoading={false}
          leftColumn={<></>}
          rightColumn={
            <Button
              variant="inverted"
              startIcon={<EmailIcon />}
              onClick={() => {
                history.push(inviteCaregiverUrl);
              }}
              disabled={!canWrite}
            >
              {t(translations.senior_caregivers_listing.invite_caregiver_button)}
            </Button>
          }
        />
      )}
      <Box
        className={`caregivers-listing-container${caregiverMode === CaregiverMode.SystemAdministrator ? '-sys' : ''}`}
      >
        <Box className="caregivers-listing-container-centered">
          <Box className="caregivers-listing-button">
            {caregiverMode === CaregiverMode.SystemAdministrator ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EmailIcon />}
                onClick={() => {
                  history.push(inviteCaregiverUrl);
                }}
                disabled={!canWrite}
              >
                {t(translations.senior_caregivers_listing.invite_caregiver_button)}
              </Button>
            ) : (
              <div></div>
            )}
            <Button
              color="primary"
              variant={caregiverMode === CaregiverMode.SystemAdministrator ? 'outlined' : 'text'}
              className={
                caregiverMode === CaregiverMode.Professional ? 'caregivers-listing-button-link' : ''
              }
              startIcon={caregiverMode === CaregiverMode.SystemAdministrator && <HistoryIcon />}
              onClick={() => {
                history.push(invitesHistoryUrl);
              }}
            >
              {t(translations.senior_caregivers_listing.invites_history_button)}
            </Button>
          </Box>
          <PaginatedTable
            avatar={{type: 'user'}}
            emptyTableMessage={t(translations.senior_caregivers_listing.no_caregivers)}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.senior_caregivers_listing.name_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.RelationshipType,
                title: t(translations.senior_caregivers_listing.relationship_type_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.Remove,
                title: t(translations.senior_caregivers_listing.remove_relationship_header),
                flexGrow: 1,
                button: {
                  type: 'text-button',
                  showColumnValue: false,
                  textButton: {
                    label: t(translations.senior_caregivers_listing.remove_relationship_button),
                    color: 'error',
                    handleClick: handleAskRemoveRelationship,
                  },
                  disabled: !canWrite,
                  disabledTooltip: !canWrite
                    ? t(translations.senior_caregivers_listing.remove_relationship_button_tooltip)
                    : undefined,
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={caregiverRowBaseLinkUrl ? true : false}
          />
        </Box>
      </Box>
      {removeDialogOpened && (
        <SeniorCaregiversListingModal
          open
          submitting={submittingRemove}
          seniorName={seniorName}
          caregiverName={relationshipToRemoveRef.current?.name}
          onClose={() => {
            setRemoveDialogOpened(false);
            relationshipToRemoveRef.current = undefined;
          }}
          onRemove={handleRemoveRelationship}
        />
      )}
    </Box>
  );
};
